import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './AllSellStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import apiService from '../../services/api';
import ViewStockAdjustmentModal from './ViewStockAdjustmentModal';

const StockAdjustmentReport = () => {
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [adjustments, setAdjustments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [totalAdjustments, setTotalAdjustments] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    // Summary metrics state
    const [summary, setSummary] = useState({
        totalNormal: 0,
        totalAbnormal: 0,
        totalStockAdjustment: 0,
        totalAmountRecovered: 0
    });

    const fetchAdjustmentData = async () => {
        setIsLoading(true);
        try {
            const response = await apiService.getStockAdjustments({
                page: currentPage,
                per_page: entriesPerPage,
                search: searchTerm
            });
            const data = response.data || response;
            const adjustmentList = Array.isArray(data) ? data : (data.data || []);
            setAdjustments(adjustmentList);
            setTotalAdjustments(data.total || adjustmentList.length);

            // Calculate summaries based on visible data or fetch from report API if available
            // For now, let's calculate from the list (though in production a specific report endpoint would be better)
            let normal = 0, abnormal = 0, total = 0, recovered = 0;
            adjustmentList.forEach(adj => {
                const amount = parseFloat(adj.final_total || adj.totalAmount || 0);
                const rec = parseFloat(adj.amount_recovered || adj.amountRecovered || 0);
                const type = (adj.adjustment_type || adj.type || '').toLowerCase();
                
                if (type === 'normal') normal += amount;
                else if (type === 'abnormal') abnormal += amount;
                
                total += amount;
                recovered += rec;
            });

            setSummary({
                totalNormal: normal,
                totalAbnormal: abnormal,
                totalStockAdjustment: total,
                totalAmountRecovered: recovered
            });

        } catch (err) {
            console.error('Failed to fetch report data', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAdjustmentData();
    }, [currentPage, entriesPerPage]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchAdjustmentData();
    };

    const handleExport = (format) => {
        const headers = ['Date', 'Reference No', 'Location', 'Adjustment type', 'Total Amount', 'Total amount recovered', 'Reason', 'Added By'];
        const rows = adjustments.map(adj => [
            adj.transaction_date || adj.date,
            adj.ref_no || adj.refNo,
            adj.business_location?.name || adj.location || 'Main Location',
            adj.adjustment_type || adj.type,
            (parseFloat(adj.final_total || adj.totalAmount || 0)).toFixed(2),
            (parseFloat(adj.amount_recovered || adj.amountRecovered || 0)).toFixed(2),
            adj.additional_notes || adj.reason || '',
            adj.created_by_name || adj.addedBy || 'N/A'
        ]);

        const title = 'Stock Adjustment Report';
        const filename = 'stock_adjustment_report';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();

        setShowExportDropdown(false);
    };

    const handleViewDetails = (adjustment) => {
        setSelectedAdjustment(adjustment);
        setShowViewModal(true);
    };

    const formatCurrency = (amount) => `$ ${parseFloat(amount).toFixed(2)}`;

    return (
        <div style={{ paddingTop: '0.5rem' }}>
            {/* ── Summary Cards ── */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '1rem',
                marginBottom: '1.5rem',
            }}>
                {/* Left card */}
                <div style={{
                    background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: '10px', padding: '1.25rem 1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                    <div className="report-metric-row">
                        <span>Total Normal:</span>
                        <strong>{formatCurrency(summary.totalNormal)}</strong>
                    </div>
                    <div className="report-metric-row">
                        <span>Total Abnormal:</span>
                        <strong>{formatCurrency(summary.totalAbnormal)}</strong>
                    </div>
                    <div className="report-metric-row total">
                        <span>Total Stock Adjustment:</span>
                        <strong>{formatCurrency(summary.totalStockAdjustment)}</strong>
                    </div>
                </div>

                {/* Right card */}
                <div style={{
                    background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: '10px', padding: '1.25rem 1.5rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <div style={{ width: '100%' }}>
                        <div className="report-metric-row large">
                            <span style={{ color: '#64748b' }}>Total Amount Recovered:</span>
                            <strong style={{ color: '#10b981', fontSize: '1.25rem' }}>{formatCurrency(summary.totalAmountRecovered)}</strong>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Stock Adjustments Table ── */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

                {/* Table title */}
                <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1e293b' }}>Stock Adjustments</span>
                    {isLoading && <div className="small-spinner"></div>}
                </div>

                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>

                    <div className="table-controls-right">
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu">
                                    <button onClick={() => handleExport('CSV')}><i className="fa fa-file-excel-o" /> Export CSV</button>
                                    <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel-o" /> Export Excel</button>
                                    <button onClick={() => handleExport('Print')}><i className="fa fa-print" /> Print</button>
                                    <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf-o" /> Export PDF</button>
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSearchSubmit}>
                            <input
                                type="text"
                                placeholder="Search ..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                style={{
                                    padding: '0.4rem 0.75rem',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '6px',
                                    fontSize: '0.8125rem',
                                    outline: 'none',
                                    width: '180px',
                                }}
                            />
                        </form>
                    </div>
                </div>

                {/* Table */}
                <div className="products-table-container">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>Action</th>
                                <th>Date</th>
                                <th>Reference No</th>
                                <th>Location</th>
                                <th>Adjustment type</th>
                                <th>Total Amount</th>
                                <th>Total amount recovered</th>
                                <th>Reason</th>
                                <th>Added By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {adjustments.length === 0 ? (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center', padding: '2.5rem', color: '#94a3b8', fontSize: '0.875rem' }}>
                                        {isLoading ? 'Loading data...' : 'No data available in table'}
                                    </td>
                                </tr>
                            ) : (
                                adjustments.map((adj) => (
                                    <tr key={adj.id}>
                                        <td>
                                            <button 
                                                className="action-btn-view" 
                                                onClick={() => handleViewDetails(adj)}
                                                style={{ padding: '0.25rem 0.75rem', borderRadius: '4px', background: '#f1f5f9', border: '1px solid #e2e8f0', fontSize: '0.75rem', cursor: 'pointer' }}
                                            >
                                                View
                                            </button>
                                        </td>
                                        <td>{adj.transaction_date || adj.date}</td>
                                        <td style={{ fontWeight: '700' }}>{adj.ref_no || adj.refNo}</td>
                                        <td>{adj.business_location?.name || adj.location || 'Main Location'}</td>
                                        <td>
                                            <span className={`adjustment-type-badge ${(adj.adjustment_type || adj.type || '').toLowerCase()}`}>
                                                {adj.adjustment_type || adj.type}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: '700' }}>{formatCurrency(adj.final_total || adj.totalAmount || 0)}</td>
                                        <td>{formatCurrency(adj.amount_recovered || adj.amountRecovered || 0)}</td>
                                        <td style={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={adj.additional_notes || adj.reason}>
                                            {adj.additional_notes || adj.reason || '--'}
                                        </td>
                                        <td>{adj.created_by_name || adj.addedBy || 'N/A'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem' }}>
                        Showing {adjustments.length > 0 ? ((currentPage - 1) * entriesPerPage) + 1 : 0} to {Math.min(currentPage * entriesPerPage, totalAdjustments)} of {totalAdjustments} entries
                    </div>
                    <div className="pagination-controls">
                        <button 
                            className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`}
                            onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                        >Previous</button>
                        <button 
                            className={`page-btn ${currentPage * entriesPerPage >= totalAdjustments ? 'disabled' : ''}`}
                            onClick={() => currentPage * entriesPerPage < totalAdjustments && setCurrentPage(currentPage + 1)}
                        >Next</button>
                    </div>
                </div>
            </div>

            {showViewModal && <ViewStockAdjustmentModal adjustment={selectedAdjustment} onClose={() => setShowViewModal(false)} />}

            <style>{`
                .report-metric-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.6rem 0;
                    border-bottom: 1px solid #f1f5f9;
                }
                .report-metric-row:last-child {
                    border-bottom: none;
                }
                .report-metric-row.total {
                    margin-top: 0.5rem;
                    padding-top: 1rem;
                    border-top: 2px solid #e2e8f0;
                }
                .report-metric-row span {
                    font-weight: 500;
                    font-size: 0.9rem;
                    color: #64748b;
                }
                .report-metric-row strong {
                    font-weight: 700;
                    font-size: 1rem;
                    color: #1e293b;
                }
                .small-spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #f3f3f3;
                    border-top: 2px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .adjustment-type-badge {
                    padding: 0.2rem 0.6rem;
                    border-radius: 999px;
                    font-size: 0.7rem;
                    font-weight: 700;
                    text-transform: uppercase;
                }
                .adjustment-type-badge.normal {
                    background: #dcfce7;
                    color: #166534;
                }
                .adjustment-type-badge.abnormal {
                    background: #fee2e2;
                    color: #991b1b;
                }
            `}</style>
        </div>
    );
};

export default StockAdjustmentReport;
